import { memo } from 'react'
import FormatCurrency from 'components/Common/FormatCurrency'
import Caption from 'LuxKit/Typography/Caption'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import type { CalendarPickerMonthView } from 'selectors/offerPage/offerPageSelectors'
import MobileMultipleRoomsDurationLabel from 'components/OfferPage/MultipleRoomsBooking/Mobile/MobileMultipleRoomsDurationLabel'
import { FormattedMessage, useIntl } from 'react-intl'

interface Props {
  duration: number
  month: CalendarPickerMonthView
  hasOtherAvailability: boolean
  enableCalendarRecommendations: boolean
  selected?: boolean
  soldOutText?: string
  hideChildPrices?: boolean
  markCheapestPrice?: boolean
  isMultiRoom?: boolean
}

function OfferCalendarHotelMonthTile(props: Props) {
  const {
    month,
    duration,
    hasOtherAvailability,
    enableCalendarRecommendations,
    selected,
    soldOutText = 'Sold out',
    hideChildPrices,
    markCheapestPrice,
    isMultiRoom,
  } = props

  const intl = useIntl()
  const subTextColour = selected ? undefined : 'neutral-three'
  const priceDefaultColour = markCheapestPrice && month.cheapest ? 'highlight-secondary' : undefined
  const priceColour = month.isLuxPlusPrice && !selected ? 'lux-plus' : priceDefaultColour

  const defaultSoldOutText = intl.formatMessage({
    id: 'a9_J1XfErNWngNwr62oI',
    defaultMessage: 'Sold out',
    description: 'Text shown when a month is sold out in calendar month picker',
  })

  const viewSimilarOffersText = intl.formatMessage({
    id: 'os5Oyb_wcznG2qIjMcHY',
    defaultMessage: 'View similar offers',
    description: 'Link text to view similar offers when a month is sold out in calendar month picker',
  })

  const availableInOtherRoomTypesText = intl.formatMessage({
    id: 'o2gwO6EDNysFXwFsBZ5L',
    defaultMessage: 'Available in other room types',
    description: 'Link text shown when room is sold out but other room types are available in month tile',
  })
  const selectCheckInDateText = intl.formatMessage({
    id: 'nST_Qwkzm7f4uwYjDakt',
    defaultMessage: 'Select check-in date',
    description: 'Label to select check-in date when child prices are hidden in month tile',
  })
  const viewAvailabilityText = intl.formatMessage({
    id: 'njgWBcnPd_1rCJ4POO0T',
    defaultMessage: 'View availability',
    description: 'Label to view availability when check-in is selected in month tile',
  })

  return (
    <div>
      {!month.hotelAvailable && <>
        <Caption variant="medium" colour={subTextColour}>{soldOutText === 'Sold out' ? defaultSoldOutText : soldOutText}</Caption>
        {!hasOtherAvailability && enableCalendarRecommendations && <TextLink size="small" weight="regular">
          {viewSimilarOffersText}
        </TextLink>}
        {hasOtherAvailability &&
          <TextLink size="small" weight="regular">
            {availableInOtherRoomTypesText}
          </TextLink>}
      </>}
      {month.hotelAvailable && <>
        {hideChildPrices && <Caption variant="medium" colour="neutral-three" underline>{!selected ? selectCheckInDateText : viewAvailabilityText}</Caption>}
        {!hideChildPrices && <div>
          {!isMultiRoom && <Caption variant="medium" colour={subTextColour}>{intl.formatMessage({
            id: 'eFQuLYehOtF6MeMi_3kT',
            defaultMessage: '{nights, plural, one {# night} other {# nights}} from',
            description: 'Night count with from label for hotel calendar month tile',
          }, { nights: duration })}
          </Caption>}
          {isMultiRoom && <Caption variant="medium" colour={subTextColour}><MobileMultipleRoomsDurationLabel shouldDisplayRoom shouldDisplayFlight={false} /></Caption>}
          <Caption variant="medium" colour={(month.isLuxPlusPrice && !selected) ? 'lux-plus' : subTextColour}>
            <FormattedMessage
              id="QfsXQzr7oAC6bGHZ9ce1"
              defaultMessage="{price} total"
              description="Total price label for hotel calendar month tile component"
              values={{
                price: <BodyText as="span" variant="medium" weight="bold" colour={priceColour}>
                  <FormatCurrency value={month.hotelTotal} />
                </BodyText>,
              }}
            />
          </Caption>
        </div>}
      </> }
    </div>
  )
}

export default memo(OfferCalendarHotelMonthTile)
